import { useEffect, useRef } from 'react';

// 04-enforce.md 4.3 -- una única suscripción compartida por tipo de evento de `window`, en vez
// de un `addEventListener`/`removeEventListener` por consumidor. Cada tipo tiene un listener a
// nivel de módulo + un `Set` de callbacks; el listener real se agrega al primer suscriptor y se
// quita cuando el último se va. Los componentes presentacionales (que tienen prohibido
// `window.addEventListener` por el boundary lint) consumen esto en su lugar.
//
// El handler del consumidor se guarda en un ref y se lee en cada disparo, así cambiar el
// handler entre renders NO re-suscribe (ni exige memoizarlo en el call site).

const registries = new Map(); // eventType -> { callbacks: Set<fn>, listener: fn, options }

function getRegistry(eventType, options) {
  const key = options && options.capture ? `${eventType}:capture` : eventType;
  let reg = registries.get(key);
  if (!reg) {
    const callbacks = new Set();
    const listener = (event) => {
      // Copia defensiva: un callback puede desuscribirse a sí mismo durante el disparo.
      for (const cb of [...callbacks]) cb(event);
    };
    reg = { callbacks, listener, eventType, options };
    registries.set(key, reg);
  }
  return reg;
}

export function useWindowEvent(eventType, handler, { enabled = true, capture = false } = {}) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return undefined;
    const reg = getRegistry(eventType, { capture });
    const cb = (event) => handlerRef.current(event);
    if (reg.callbacks.size === 0) {
      window.addEventListener(reg.eventType, reg.listener, capture ? { capture: true } : undefined);
    }
    reg.callbacks.add(cb);
    return () => {
      reg.callbacks.delete(cb);
      if (reg.callbacks.size === 0) {
        window.removeEventListener(reg.eventType, reg.listener, capture ? { capture: true } : undefined);
      }
    };
  }, [eventType, enabled, capture]);
}

// Convenience: run `onEscape` when Escape is pressed (the single most common `keydown` use in
// this codebase — modals/overlays closing).
export function useEscapeKey(onEscape, enabled = true) {
  useWindowEvent('keydown', (e) => { if (e.key === 'Escape') onEscape(e); }, { enabled });
}

// Convenience: re-run `reposition` whenever the viewport changes (scroll — captured, so it also
// fires for scrolls inside nested containers — or resize). The celda popovers use this to keep
// an absolutely-positioned dropdown glued to its trigger.
export function useRepositionOnViewportChange(reposition, enabled = true) {
  useWindowEvent('scroll', reposition, { enabled, capture: true });
  useWindowEvent('resize', reposition, { enabled });
}
